import { useEffect, useRef, TransitionEvent, Suspense, lazy } from 'react';
import classNames from 'classnames';

import { sendEventV2 } from '@/utils/analytics';
import { pathToURL } from '@/utils/urls';
import { useAppDispatch, useAppSelector } from '@/state/store';
import { useAudio } from '@/hooks/audio';
import { useTheme } from '@/theme';
import { setIsInformationOpen } from '@/state/features/app';
import IconButton from '@/components/IconButton';

import * as styles from './styles.module.scss';

// Import custom content per instance if needed, otherwise use generic
const InfoDrawerContent = lazy(() => {
    switch (process.env.PUBLIC_INSTANCE) {
        // case 'example':
        //     return import('./content/example');
        default:
            return import('./content/information-drawer-content-generic');
    }
});
export default function InformationDrawer() {
    const dispatch = useAppDispatch();
    const { audio } = useAudio();
    const theme = useTheme();
    const isInformationOpen = useAppSelector((state) => state.app.isInformationOpen);
    const drawerRef = useRef<HTMLDivElement>(null);
    const drawerData = theme.metadata?.customJSON?.informationDrawer;

    useEffect(() => {
        if (isInformationOpen) {
            audio?.play('gust-open');
            sendEventV2('information_drawer_viewed');
        }
    }, [isInformationOpen]);

    // Move focus to drawer when opened
    function onTransitionEnd(e: TransitionEvent<HTMLDivElement>) {
        if (isInformationOpen && e.target === e.currentTarget) {
            setTimeout(() => {
                drawerRef.current?.focus();
            }, 200);
        }
    }

    if (!drawerData?.enabled) return null;
    return (
        <div
            role="dialog"
            aria-label="Information"
            ref={drawerRef}
            tabIndex={-1}
            className={classNames(styles.InformationDrawer, {
                [styles.isOpen]: isInformationOpen,
            })}
            inert={!isInformationOpen ? '' : undefined}
            onTransitionEnd={onTransitionEnd}
        >
            <div
                className={styles.scrollContainer}
                style={{ backgroundImage: `url("${pathToURL(drawerData?.topBackgroundImage?.imagePath)}")` }}
            >
                <IconButton
                    iconType="close"
                    buttonStyle="round"
                    ariaLabel="Close Information"
                    className={styles.closeButton}
                    onClick={() => {
                        audio?.play('gust-close');
                        dispatch(setIsInformationOpen(false));
                    }}
                />
                <div className={styles.contentContainer}>
                    <Suspense fallback={<div>Loading</div>}>
                        <InfoDrawerContent />
                    </Suspense>
                </div>
            </div>
        </div>
    );
}
